import { motion } from 'framer-motion';
import { initialGoDown, whileInView, transition, initialGoUp } from '../../helpers/animations';
import { useTranslation } from 'react-i18next';
import { useHomeTranslationsQuery } from '../../services/HomePageServices';
import { SponsorGroupKey, SponsorType } from '../../types/SponsorType';

const getSponsorGroupKey = (group: SponsorType): SponsorGroupKey | undefined => group.type ?? group.value;
const CMS_LANGUAGE_CODE_BY_APP_LANGUAGE: Record<string, string> = {
  en_HK: 'en-US',
  sc_CN: 'zh-SC',
  zh_HK: 'zh-TC'
};
const SPONSOR_GROUP_ROWS: SponsorGroupKey[][] = [
  ['innovate', 'collaboration'],
  ['built'],
  ['support']
];

function SponsorSection() {
  const { i18n } = useTranslation('sponsor');
  const homeQuery = useHomeTranslationsQuery();
  const cmsLanguageCode = CMS_LANGUAGE_CODE_BY_APP_LANGUAGE[i18n.language] ?? i18n.language;
  const sponsorTrans = homeQuery.data
    ? homeQuery.data.find((translation) => translation.languages_code === cmsLanguageCode) ?? homeQuery.data[0]
    : undefined;
  const isLoading = homeQuery.isLoading;

  if (isLoading || !sponsorTrans) return null;

  const sponsorGroups = sponsorTrans.sponsor_type
    .map((group) => {
      const groupKey = getSponsorGroupKey(group);
      return {
        groupKey,
        title: group.title,
        logos: groupKey
          ? sponsorTrans.sponsor_logo.filter((logo) => logo.type === groupKey)
          : []
      };
    })
    .filter((group) => group.groupKey && group.title && group.logos.length > 0);

  const sponsorGroupsByKey = new Map(
    sponsorGroups
      .filter((group): group is typeof group & { groupKey: SponsorGroupKey } => !!group.groupKey)
      .map((group) => [group.groupKey, group])
  );

  const sponsorRows = SPONSOR_GROUP_ROWS
    .map((row) => row
      .map((groupKey) => sponsorGroupsByKey.get(groupKey))
      .filter((group): group is NonNullable<typeof group> => !!group))
    .filter((row) => row.length > 0);

  return (
    <section className="sponsor-section">
      <div className="sponsor-section__container">
        <motion.h2
          initial={initialGoDown}
          whileInView={whileInView}
          viewport={{
            once: true,
            amount: 0.2
          }}
          transition={transition}
          className="sponsor-section__title"
        >
          {sponsorTrans.sponsor_title}
        </motion.h2>
        <motion.div
          initial={initialGoUp}
          whileInView={whileInView}
          viewport={{
            once: true,
            amount: 0.2
          }}
          transition={transition}
          className="sponsor-section__groups"
        >
          {sponsorRows.map((row, rowIndex) => (
            <div key={`sponsor-row-${rowIndex}`} className="sponsor-section__row">
              {row.map((group) => (
                <div key={group.groupKey} className="sponsor-section__group">
                  <div className="sponsor-section__group-title">{group.title}</div>
                  <div className="sponsor-section__logos">
                    {group.logos.map((logo, index) => (
                      <SponsorLogo
                        key={`${group.groupKey}-${logo.name}-${index}`}
                        name={logo.name}
                        logo={logo.logo}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

export default SponsorSection;

type SponsorLogoProps = {
  name: string;
  logo: string;
};

function SponsorLogo({ name, logo }: SponsorLogoProps) {
  return (
    <div className="sponsor-section__sponsor-logo">
      <img alt={name} src={logo} />
    </div>
  );
}
